import { Button, Input, Select, TableProps } from "antd";
import { useEffect, useState } from "react";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import RequestDateWithOutDateModal from "../../../components/Report/RequestDateWithOutDateModal";
import CustomTable from "../../../components/ui/CustomTable";
import { API_URL } from "../../../constants/apiUrl";
import { PAYROLL_API } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import {
  useLazyDownloadTaxReportSingleQuery,
  useTaxReportListQuery,
} from "../../../features/payroll/payrollApi";
import { useDownloadTaxReportMutation } from "../../../features/report/reportApi";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  GetTaxReportExportResponseType,
  GetTaxReportResponseListType,
  GetTaxReportResponseParamsType,
} from "../../../types/payroll";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  formatSouthAsianWithDecimals,
  selectArrayOption,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import { payrollBetaSettingsTabList } from "./PayrollSummary";

type TexReportDetailsType =
  GetTaxReportResponseListType["data"]["tax_report_list"][0];

export default function TaxReport() {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { open, handleClose, handleOpen } = useModal<
    string,
    "REPORT_REQUEST"
  >();
  const {
    page,
    limit,
    keyword,
    handleSearch,
    filter,
    handleFilter,
    handleLimit,
    handlePagination,
  } = useTable<GetTaxReportResponseParamsType>({
    defaultFilter: {
      search_type: "name",
    },
  });
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const { isLoading, isError, error, data, isFetching } = useTaxReportListQuery(
    {
      params: {
        ...filter,
        search_type: undefined,
        ...(keyword ? { search_type: filter.search_type } : {}),
        search: keyword,
        department_id: filter.department_id,
        skip: undefined,
        page,
      },
    }
  );
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const [downloadTaxReportSingle, { isLoading: isDownloading, data: pdfUrl }] =
    useLazyDownloadTaxReportSingleQuery();
  const [
    downloadTaxReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useDownloadTaxReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get tax report data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");

  // const taxReportData = data?.data?.formattedEmployees?.map((item) => ({
  //   ...item,
  //   ...calculateTax(Number(item?.gross_salary), item?.gender),
  // }));

  const columns: TableProps<TexReportDetailsType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "ID",
      dataIndex: "employee_id",
      width: 120,
      sorter: (a: any, b: any) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-nowrap ">
          {val ?? "n/a"}
        </span>
      ),
    },
    {
      title: <div className="text-nowrap w-[105px]">Employee Name</div>,
      dataIndex: "name",
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <span className="flex items-center gap-1.5">{val ?? "n/a"}</span>
      ),
    },
    {
      title: <div className="text-nowrap w-[175px]">Department</div>,
      dataIndex: "department",
      render: (val) => val || "n/a",
    },
    {
      title: <div className="text-nowrap w-[140px]">Total Taxable Income</div>,
      sorter: (a, b) =>
        Number(a?.tax_details?.total_taxable_income ?? 0) -
        Number(b?.tax_details?.total_taxable_income ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) =>
        formatSouthAsianWithDecimals(val?.tax_details?.total_taxable_income),
    },
    // {
    //   title: <div className="text-nowrap w-[125px]">Exemption Amount</div>,
    //   sorter: (a, b) =>
    //     Number(a?.tax_details?.exemption_amount ?? 0) -
    //     Number(b?.tax_details?.exemption_amount ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) => formatSouthAsianWithDecimals(val?.tax_details?.exemption_amount),
    // },
    // {
    //   title: (
    //     <div className="text-nowrap w-[175px]">Remaining Taxable Income</div>
    //   ),
    //   sorter: (a, b) =>
    //     Number(a?.tax_details?.remaining_taxable_income ?? 0) -
    //     Number(b?.tax_details?.remaining_taxable_income ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) =>
    //     formatSouthAsianWithDecimals(val?.tax_details?.remaining_taxable_income),
    // },
    // {
    //   title: <div className="text-nowrap w-[50px]">5% Slab</div>,
    //   dataIndex: "slab5",
    //   sorter: (a, b) => Number(a?.slab5 ?? 0) - Number(b?.slab5 ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) => formatSouthAsianWithDecimals(val),
    // },
    // {
    //   title: <div className="text-nowrap w-[60px]">10% Slab</div>,
    //   dataIndex: "slab10",
    //   sorter: (a, b) => Number(a?.slab10 ?? 0) - Number(b?.slab10 ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) => formatSouthAsianWithDecimals(val),
    // },
    // {
    //   title: <div className="text-nowrap w-[60px]">15% Slab</div>,
    //   dataIndex: "slab15",
    //   sorter: (a, b) => Number(a?.slab15 ?? 0) - Number(b?.slab15 ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) => formatSouthAsianWithDecimals(val),
    // },
    // {
    //   title: <div className="text-nowrap w-[60px]">20% Slab</div>,
    //   dataIndex: "slab20",
    //   sorter: (a, b) => Number(a?.slab20 ?? 0) - Number(b?.slab20 ?? 0),
    //   sortIcon: () => <SortIcon />,
    //   render: (val) => formatSouthAsianWithDecimals(val),
    // },
    ...Object.keys(
      data?.data?.tax_report_list[0]?.tax_details?.slabs || {}
    ).map((key) => ({
      title: <div className="text-nowrap w-[60px]">{key}</div>,
      sorter: (a: any, b: any) =>
        Number(a?.tax_details?.slabs?.[key] ?? 0) -
        Number(b?.tax_details?.slabs?.[key] ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val: any) =>
        formatSouthAsianWithDecimals(val?.tax_details?.slabs?.[key] || 0),
    })),
    {
      title: (
        <div className="text-nowrap w-[170px]">Total Tax Amount (Yearly)</div>
      ),
      sorter: (a, b) =>
        Number(a?.tax_details?.total_tax_amount_yearly ?? 0) -
        Number(b?.tax_details?.total_tax_amount_yearly ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) =>
        formatSouthAsianWithDecimals(val?.tax_details?.total_tax_amount_yearly),
    },
    {
      title: (
        <div className="text-nowrap w-[185px]">Total Tax Amount (Monthly)</div>
      ),
      sorter: (a, b) =>
        Number(a?.tax_details?.total_tax_amount_monthly ?? 0) -
        Number(b?.tax_details?.total_tax_amount_monthly ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) =>
        formatSouthAsianWithDecimals(
          val?.tax_details?.total_tax_amount_monthly
        ),
    },
    {
      title: "Action",
      dataIndex: "business_member_id",
      key: "action",
      render: (val) => (
        <Button
          loading={loadingId === val} // Show loading only for the clicked button
          onClick={async () => {
            setLoadingId(val); // Set loading for the current button
            try {
              await downloadTaxReportSingle({
                business_memeber_id: val,
              });
            } finally {
              setLoadingId(null); // Reset loading after completion
            }
          }}
          className="action-button !border-none"
          size="small"
          icon={
            <span className="!text-primary">
              <DownloadIcon />
            </span>
          }
        />
      ),
    },
  ];

  let serialNumber = 1;

  const handleDownload = () => {
    downloadXlsx(
      PAYROLL_API + API_URL.taxReportList,
      {
        skip: 0,
        limit: 0,
      },
      "Tax Report",
      "tax_report_list",
      (
        tax_report: GetTaxReportExportResponseType["data"]["tax_report_list"][0]
      ) => {
        const breakdown = tax_report?.breakdown_percentage || [];
        const breakdownColumns: Record<string, string | number> = {};
        breakdown.forEach((item) => {
          breakdownColumns[item.name] = item.amount ?? "-";
        });

        const slabs =
          (tax_report?.tax_details?.slabs as Record<string, number>) || {};
        const slabColumns: Record<string, string | number> = {};
        Object.keys(slabs).forEach((key) => {
          slabColumns[key] = slabs[key] ?? "-";
        });

        return {
          "SL No.": serialNumber++,
          "Employee ID": tax_report?.employee_id ?? "-",
          Name: tax_report?.name ?? "-",
          Department: tax_report?.department ?? "-",
          Gender: tax_report?.gender ?? "-",
          Designation: tax_report?.designation ?? "-",
          "Joining Date": tax_report?.join_date ?? "-",
          "Gross Salary": tax_report?.gross_salary ?? "-",
          ...breakdownColumns,
          "Taxable Income": tax_report?.tax_details?.total_taxable_income,
          "Total Tax Monthly":
            tax_report?.tax_details?.total_tax_amount_monthly,
          "Total Tax Yearly": tax_report?.tax_details?.total_tax_amount_yearly,
          ...slabColumns,
        };
      }
    );
  };

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "tax_certificate.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pdfUrl]);

  return (
    <SidebarLayout title="Payroll" tabList={payrollBetaSettingsTabList}>
      {open.type === "REPORT_REQUEST" && (
        <RequestDateWithOutDateModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          // detailsMessage={`You are requesting a Report Export range for ${dayjs(
          //   filter?.date,
          //   dbDateFormat
          // ).format("DD/MM/YYYY")}`}
          handleSubmit={() => {
            downloadTaxReport({
              body: {
                name: "Tax report",
              },
              params: { ...filter, skip: 0, limit: 0 },
            });
          }}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching}
        page={page}
        limit={limit}
        columns={columns}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        dataSource={addKeyInArray(
          data?.data?.tax_report_list,
          "business_member_id"
        )}
        total={data?.data?.total}
        filterSection={
          <div className="flex flex-col items-end flex-wrap gap-2">
            <div className="flex flex-wrap gap-2">
              <Select
                showSearch
                placeholder="Search Type"
                value={filter.search_type}
                options={searchTypeList}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Input
                key={filter.search_type}
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter.search_type || "",
                  true
                )}`}
                style={{ minWidth: 170, maxWidth: 220 }}
                onChange={(val) => handleSearch(val)}
              />

              {/* <DatePicker
                format={dateFormat}
                style={{ width: 140 }}
                suffixIcon={<DateIcon />}
                className="clear-button-hide"
              /> */}
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(val) => {
                  handleFilter("department_id", val);
                }}
              />
              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Report Requests
              </Button>
              {/* <Button
                className="download-button"
                icon={<DownloadIcon />}
                loading={excelLoading}
                onClick={handleDownload}
              >
                Export
              </Button> */}
            </div>
            <div className="text-[#CB5B2A] bg-[#FFD3C0] rounded-md py-2 px-3 font-semibold">
              Total TAX: BDT{" "}
              {formatSouthAsianWithDecimals(
                data?.data?.total_tax_yearly_bdt || 0
              )}
            </div>
          </div>
        }
      />
    </SidebarLayout>
  );
}

const searchTypeList = [
  { label: "Name", value: "name" },
  {
    label: "Employee ID",
    value: "employee_id",
  },
];
